import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes } from 'lucide-react';
import { Container, PageHeader, ActionBar } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateBucket, useBucketNameCheck, s3ExtractError } from '@/api/s3';
import { validateBucketName } from './bucket-name-validation';

// Regions offered at create time (Wasabi region set). Default is the account's
// home region ap-southeast-1, labelled Vietnam/Singapore.
const REGIONS = [
  { value: 'ap-southeast-1', label: 'Asia Pacific · Vietnam/Singapore (ap-southeast-1)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific · Tokyo (ap-northeast-1)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific · Osaka (ap-northeast-2)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific · Sydney (ap-southeast-2)' },
  { value: 'us-east-1', label: 'US East · N. Virginia (us-east-1)' },
  { value: 'us-east-2', label: 'US East · N. Virginia (us-east-2)' },
  { value: 'us-central-1', label: 'US Central · Texas (us-central-1)' },
  { value: 'us-west-1', label: 'US West · Oregon (us-west-1)' },
  { value: 'eu-central-1', label: 'EU · Amsterdam (eu-central-1)' },
  { value: 'eu-central-2', label: 'EU · Frankfurt (eu-central-2)' },
  { value: 'eu-west-1', label: 'EU · United Kingdom (eu-west-1)' },
  { value: 'eu-west-2', label: 'EU · Paris (eu-west-2)' },
  { value: 'ca-central-1', label: 'Canada · Toronto (ca-central-1)' },
];

/** A borderless label/description + control row for use inside a Card body
 * (SettingRow is self-bordered, so it can't nest cleanly inside a Card). */
function ToggleRow({
  title,
  description,
  control,
  divider,
}: {
  title: string;
  description: string;
  control: React.ReactNode;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--vn-space-5)',
        paddingTop: divider ? 'var(--vn-space-4)' : undefined,
        borderTop: divider ? '1px solid var(--vn-border-subtle)' : undefined,
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>{title}</span>
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          {description}
        </span>
      </div>
      {control}
    </div>
  );
}

/** Create a bucket — name + region + versioning + object lock. */
export function BucketCreate({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const createBucket = useCreateBucket(serviceId);

  const [name, setName] = useState('');
  const [region, setRegion] = useState('ap-southeast-1');
  const [versioning, setVersioning] = useState(false);
  const [objectLock, setObjectLock] = useState(false);
  const [touched, setTouched] = useState(false);

  const nameError = touched ? validateBucketName(name) : null;

  // Debounce the name, then check GLOBAL availability (Wasabi/S3 names are
  // unique across all accounts) so the user learns a name is taken before
  // submitting rather than via a create that 409s.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(name), 350);
    return () => clearTimeout(t);
  }, [name]);
  const nameValid = !validateBucketName(name);
  const settled = nameValid && debounced === name;
  const check = useBucketNameCheck(serviceId, debounced, settled);
  const checking = settled && check.isFetching;
  const taken = settled && check.data ? !check.data.available : false;
  const available = settled && check.data?.available === true;
  const nameFieldError = nameError ?? (taken ? 'This bucket name is already taken.' : null);

  // Object Lock requires versioning — enabling it forces versioning on.
  const setLock = (v: boolean) => {
    setObjectLock(v);
    if (v) setVersioning(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (validateBucketName(name) || taken || checking) return;
    createBucket.mutate(
      { name, region, versioning, object_lock: objectLock },
      {
        onSuccess: () => {
          toast.success('Bucket created');
          navigate(`/services/oss/${serviceId}/buckets`);
        },
        onError: (e2) => toast.error(s3ExtractError(e2, 'Failed to create bucket')),
      },
    );
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        title="Create Bucket"
        description="Provision a new S3-compatible object bucket in this service."
      />
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card flat>
          <CardHeader>
            <CardTitle>Bucket</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 560 }}>
              <Field
                label="Bucket name"
                required
                error={nameFieldError ?? undefined}
                description={
                  checking ? 'Checking availability…' : available ? 'Name is available.' : undefined
                }
              >
                {(p) => (
                  <Input
                    {...p}
                    placeholder="my-media-assets"
                    value={name}
                    invalid={!!nameFieldError}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => setTouched(true)}
                  />
                )}
              </Field>

              <Field label="Region" required description={`Endpoint: s3.${region}.wasabisys.com`}>
                {(p) => (
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Data protection</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <ToggleRow
                title="Versioning"
                description="Keep multiple versions of each object."
                control={<Switch checked={versioning} disabled={objectLock} onCheckedChange={setVersioning} />}
              />
              <ToggleRow
                title="Object Lock"
                description="WORM immutability — can only be set at creation; enables versioning."
                control={<Switch checked={objectLock} onCheckedChange={setLock} />}
                divider
              />
            </div>
          </CardBody>
        </Card>

        <ActionBar
          start={
            <Button
              type="button"
              variant="secondary"
              disabled={createBucket.isPending}
              onClick={() => navigate(`/services/oss/${serviceId}/buckets`)}
            >
              Cancel
            </Button>
          }
          end={
            <Button
              type="submit"
              variant="primary"
              disabled={createBucket.isPending || taken || checking}
            >
              {createBucket.isPending ? 'Creating…' : 'Create Bucket'}
            </Button>
          }
        />
      </form>
    </Container>
  );
}
